// Couche de données Super Admin — source de vérité : Supabase.
//
// Le Super Admin lit les comptes RÉELS de la plateforme (table `comptes`) et
// leurs rattachements réels (agences, propriétaires, immeubles, biens,
// locataires, contrats, employés, abonnements). Le journal d'audit provient de
// la table `journal_audit`.
//
// Aucune donnée fictive : les objets retournés ne contiennent que ce qui existe
// réellement en base. Les colonnes absentes du schéma (ville, notes, responsable,
// renouvellement...) ne sont pas inventées.

import { supabase } from "@/store/shared/supabase";
import {
  AUDIT_ACTION_ICONS,
  AUDIT_ACTION_LABELS,
  EMPTY_RATTACHEMENTS,
  type ActiviteEntry,
  type AuditActionKey,
  type AuditEntry,
  type Compte,
  type CompteInput,
  type ComptePaiement,
  type CompteRattachements,
  type CompteStatut,
  type CompteType,
} from "@/pages/super-admin/data/types";

type Row = Record<string, unknown>;
const str = (value: unknown): string => (typeof value === "string" ? value : "");
const num = (value: unknown): number => (typeof value === "number" ? value : Number(value ?? 0) || 0);
const rows = (value: unknown): Row[] => (Array.isArray(value) ? (value as Row[]) : []);

function typeFromRole(role: string): CompteType {
  if (role === "dg_proprietaire") return "proprietaire";
  if (role === "dg_agence") return "agence";
  return "utilisateur";
}

function statutFromDb(statut: string): CompteStatut {
  if (
    statut === "actif" ||
    statut === "en_attente" ||
    statut === "suspendu" ||
    statut === "desactive" ||
    statut === "refuse"
  ) {
    return statut;
  }
  return "en_attente";
}

function actionKeyFromText(action: string): AuditActionKey {
  const value = action.toLowerCase();
  if (value.includes("refus")) return "refus";
  if (value.includes("suspend")) return "suspension";
  if (value.includes("désactiv") || value.includes("desactiv")) return "desactivation";
  if (value.includes("réactiv") || value.includes("reactiv")) return "reactivation";
  if (value.includes("activ")) return "activation";
  if (value.includes("modif")) return "modification";
  return "creation";
}

function paiementStatutFromDb(statut: string): ComptePaiement["statut"] {
  if (statut === "valide" || statut === "rejete" || statut === "annule") return statut;
  return "en_attente";
}

function groupBy<T>(list: T[], key: (item: T) => string | null): Map<string, T[]> {
  const map = new Map<string, T[]>();
  list.forEach((item) => {
    const bucketKey = key(item);
    if (!bucketKey) return;
    const bucket = map.get(bucketKey);
    if (bucket) bucket.push(item);
    else map.set(bucketKey, [item]);
  });
  return map;
}

export interface SuperAdminSnapshot {
  comptes: Compte[];
  audit: AuditEntry[];
}

export async function fetchSuperAdminData(): Promise<SuperAdminSnapshot> {
  const [
    comptesRes,
    agencesRes,
    proprietairesRes,
    immeublesRes,
    biensRes,
    locatairesRes,
    contratsRes,
    employesRes,
    abonnementsRes,
    paiementsAboRes,
    formulesRes,
    auditRes,
  ] = await Promise.all([
    supabase.from("comptes").select("id, nom_complet, email, telephone, role, statut, formule_id, created_at, updated_at"),
    supabase.from("agences").select("id, compte_id, nom"),
    supabase.from("proprietaires").select("id, compte_id, agence_id, nom_complet, telephone, adresse"),
    supabase.from("immeubles").select("id, proprietaire_id, agence_id, nom, adresse"),
    supabase.from("biens").select("id, immeuble_id, reference, loyer_mensuel"),
    supabase.from("locataires").select("id, agence_id, proprietaire_id, prenom, nom, telephone"),
    supabase.from("contrats").select("id, bien_id, statut, loyer_mensuel"),
    supabase.from("employes_agence").select("id, agence_id, prenom, nom, fonction"),
    supabase.from("abonnements").select("id, proprietaire_id, agence_id, formule_id, statut, created_at"),
    supabase.from("paiements_abonnements").select("id, abonnement_id, montant, mode_paiement, reference, statut, date_paiement"),
    supabase.from("formules").select("id, code"),
    supabase
      .from("journal_audit")
      .select("id, compte_id, action, nouvelle_valeur, created_at")
      .order("created_at", { ascending: false })
      .limit(500),
  ]);

  const firstError = [
    comptesRes,
    agencesRes,
    proprietairesRes,
    immeublesRes,
    biensRes,
    locatairesRes,
    contratsRes,
    employesRes,
    abonnementsRes,
    paiementsAboRes,
    formulesRes,
    auditRes,
  ]
    .map((result) => result.error)
    .find(Boolean);
  if (firstError) throw firstError;

  const comptesRows = rows(comptesRes.data);
  const agenceRows = rows(agencesRes.data);
  const proprioRows = rows(proprietairesRes.data);
  const immeubleRows = rows(immeublesRes.data);
  const bienRows = rows(biensRes.data);
  const locataireRows = rows(locatairesRes.data);
  const contratRows = rows(contratsRes.data);
  const employeRows = rows(employesRes.data);
  const abonnementRows = rows(abonnementsRes.data);
  const paiementAboRows = rows(paiementsAboRes.data);
  const formuleRows = rows(formulesRes.data);
  const auditRows = rows(auditRes.data);

  const formuleCodeById = new Map(formuleRows.map((row) => [str(row.id), str(row.code)]));
  const agenceByCompte = new Map(agenceRows.map((row) => [str(row.compte_id), row]));
  const proprioByCompte = new Map(proprioRows.map((row) => [str(row.compte_id), row]));
  const immeubleNomById = new Map(immeubleRows.map((row) => [str(row.id), str(row.nom)]));
  const bienById = new Map(bienRows.map((row) => [str(row.id), row]));

  const immeublesByProprio = groupBy(immeubleRows, (row) => str(row.proprietaire_id) || null);
  const immeublesByAgence = groupBy(immeubleRows, (row) => str(row.agence_id) || null);
  const biensByImmeuble = groupBy(bienRows, (row) => str(row.immeuble_id) || null);
  const locatairesByProprio = groupBy(locataireRows, (row) => str(row.proprietaire_id) || null);
  const locatairesByAgence = groupBy(locataireRows, (row) => str(row.agence_id) || null);
  const contratsByBien = groupBy(contratRows, (row) => str(row.bien_id) || null);
  const employesByAgence = groupBy(employeRows, (row) => str(row.agence_id) || null);
  const propriosByAgence = groupBy(proprioRows, (row) => str(row.agence_id) || null);
  const paiementsByAbonnement = groupBy(paiementAboRows, (row) => str(row.abonnement_id) || null);

  // Abonnement le plus récent par propriétaire / agence (pour déduire la formule).
  const abonnementByOwner = new Map<string, Row>();
  const abonnementIdByOwner = new Map<string, string>();
  abonnementRows.forEach((row) => {
    const ownerKey = str(row.proprietaire_id) || str(row.agence_id);
    if (!ownerKey) return;
    const current = abonnementByOwner.get(ownerKey);
    if (!current || new Date(str(row.created_at)).getTime() > new Date(str(current.created_at)).getTime()) {
      abonnementByOwner.set(ownerKey, row);
      abonnementIdByOwner.set(ownerKey, str(row.id));
    }
  });

  const compteNomById = new Map(comptesRows.map((row) => [str(row.id), str(row.nom_complet)]));
  const compteTypeById = new Map(comptesRows.map((row) => [str(row.id), typeFromRole(str(row.role))]));

  function ownerKeyForCompte(compteId: string, type: CompteType): string | null {
    if (type === "proprietaire") {
      const proprio = proprioByCompte.get(compteId);
      return proprio ? str(proprio.id) : null;
    }
    if (type === "agence") {
      const agence = agenceByCompte.get(compteId);
      return agence ? str(agence.id) : null;
    }
    return null;
  }

  function rattachementsFor(compte: Row, type: CompteType): CompteRattachements {
    if (type === "proprietaire") {
      const proprio = proprioByCompte.get(str(compte.id));
      const immeubles = proprio ? immeublesByProprio.get(str(proprio.id)) ?? [] : [];
      const biens = immeubles.flatMap((immeuble) => biensByImmeuble.get(str(immeuble.id)) ?? []);
      const locataires = proprio ? locatairesByProprio.get(str(proprio.id)) ?? [] : [];
      const contrats = biens.flatMap((bien) => contratsByBien.get(str(bien.id)) ?? []);
      return {
        ...EMPTY_RATTACHEMENTS,
        immeubles: immeubles.map((immeuble) => ({
          id: str(immeuble.id),
          nom: str(immeuble.nom),
          ville: str(immeuble.adresse),
        })),
        biens: biens.map((bien) => ({
          id: str(bien.id),
          reference: str(bien.reference),
          immeubleNom: immeubleNomById.get(str(bien.immeuble_id)) ?? "",
          loyer: num(bien.loyer_mensuel),
        })),
        locataires: locataires.map((locataire) => ({
          id: str(locataire.id),
          nom: `${str(locataire.prenom)} ${str(locataire.nom)}`.trim(),
          telephone: str(locataire.telephone),
        })),
        contrats: contrats.map((contrat) => {
          const bien = bienById.get(str(contrat.bien_id));
          return {
            id: str(contrat.id),
            reference: `CTR-${str(contrat.id).slice(0, 6).toUpperCase()}`,
            bienReference: bien ? str(bien.reference) : "",
            loyer: num(contrat.loyer_mensuel),
            statut: str(contrat.statut),
          };
        }),
      };
    }

    if (type === "agence") {
      const agence = agenceByCompte.get(str(compte.id));
      const immeubles = agence ? immeublesByAgence.get(str(agence.id)) ?? [] : [];
      const biens = immeubles.flatMap((immeuble) => biensByImmeuble.get(str(immeuble.id)) ?? []);
      const proprietaires = agence ? propriosByAgence.get(str(agence.id)) ?? [] : [];
      const locataires = agence ? locatairesByAgence.get(str(agence.id)) ?? [] : [];
      const employes = agence ? employesByAgence.get(str(agence.id)) ?? [] : [];
      return {
        ...EMPTY_RATTACHEMENTS,
        proprietaires: proprietaires.map((proprio) => ({
          id: str(proprio.id),
          nom: str(proprio.nom_complet),
          telephone: str(proprio.telephone),
        })),
        immeubles: immeubles.map((immeuble) => ({
          id: str(immeuble.id),
          nom: str(immeuble.nom),
          ville: str(immeuble.adresse),
        })),
        biens: biens.map((bien) => ({
          id: str(bien.id),
          reference: str(bien.reference),
          immeubleNom: immeubleNomById.get(str(bien.immeuble_id)) ?? "",
          loyer: num(bien.loyer_mensuel),
        })),
        locataires: locataires.map((locataire) => ({
          id: str(locataire.id),
          nom: `${str(locataire.prenom)} ${str(locataire.nom)}`.trim(),
          telephone: str(locataire.telephone),
        })),
        employes: employes.map((employe) => ({
          id: str(employe.id),
          nom: `${str(employe.prenom)} ${str(employe.nom)}`.trim(),
          role: str(employe.fonction),
        })),
      };
    }

    return { ...EMPTY_RATTACHEMENTS };
  }

  // --- Journal d'audit ---
  const audit: AuditEntry[] = auditRows.map((row) => {
    const compteId = str(row.compte_id);
    const action = str(row.action);
    const nouvelle = (row.nouvelle_valeur ?? {}) as Record<string, unknown>;
    return {
      id: str(row.id),
      administrateur: str(nouvelle.administrateur) || "Super Admin",
      action: action || "Action administrative",
      actionKey: actionKeyFromText(action),
      compteId,
      compteNom: compteNomById.get(compteId) ?? "Compte supprimé",
      compteType: compteTypeById.get(compteId) ?? "utilisateur",
      statut: "reussie",
      date: str(row.created_at),
    };
  });

  const auditByCompte = groupBy(audit, (entry) => entry.compteId || null);

  // --- Comptes ---
  const comptes: Compte[] = comptesRows.map((row) => {
    const id = str(row.id);
    const role = str(row.role);
    const type = typeFromRole(role);
    const rattachements = rattachementsFor(row, type);
    const proprio = type === "proprietaire" ? proprioByCompte.get(id) : undefined;

    const ownerKey = ownerKeyForCompte(id, type);
    const abonnementId = ownerKey ? abonnementIdByOwner.get(ownerKey) : undefined;
    const abonnement = ownerKey ? abonnementByOwner.get(ownerKey) : undefined;
    // Formule réellement choisie à l'inscription (référence persistée sur le
    // compte, résolue via `formules.code`). À défaut, on retombe sur la formule
    // de l'abonnement existant. Jamais de formule inventée par défaut.
    const formuleFromCompte = formuleCodeById.get(str(row.formule_id)) ?? "";
    const formuleFromAbonnement = abonnement ? formuleCodeById.get(str(abonnement.formule_id)) ?? "" : "";
    const formuleId = formuleFromCompte || formuleFromAbonnement;

    const paiements: ComptePaiement[] = abonnementId
      ? (paiementsByAbonnement.get(abonnementId) ?? []).map((paiement) => ({
          id: str(paiement.id),
          montant: num(paiement.montant),
          methode: str(paiement.mode_paiement),
          reference: str(paiement.reference),
          date: str(paiement.date_paiement),
          statut: paiementStatutFromDb(str(paiement.statut)),
        }))
      : [];

    const activite: ActiviteEntry[] = (auditByCompte.get(id) ?? []).slice(0, 20).map((entry) => ({
      id: entry.id,
      action: entry.action,
      description: "",
      icone: AUDIT_ACTION_ICONS[entry.actionKey],
      date: entry.date,
    }));

    return {
      id,
      type,
      nom: str(row.nom_complet),
      email: str(row.email),
      telephone: str(row.telephone),
      ville: proprio ? str(proprio.adresse) : "",
      responsable: "",
      role,
      formuleId,
      statut: statutFromDb(str(row.statut)),
      notes: "",
      createdAt: str(row.created_at),
      derniereActivite: str(row.updated_at) || str(row.created_at),
      rattachements,
      paiements,
      activite,
    };
  });

  return { comptes, audit };
}

/* ------------------------------------------------------------------ */
/* Écritures                                                           */
/* ------------------------------------------------------------------ */

async function insertAudit(compteId: string, action: string, administrateur: string): Promise<void> {
  const { error } = await supabase.from("journal_audit").insert({
    compte_id: compteId,
    action,
    table_cible: "comptes",
    nouvelle_valeur: { administrateur },
  });
  if (error) throw error;
}

export async function updateCompteStatut(
  compteId: string,
  statut: CompteStatut,
  actionKey: AuditActionKey,
  administrateur: string
): Promise<void> {
  const { error } = await supabase
    .from("comptes")
    .update({ statut, actif: statut === "actif" })
    .eq("id", compteId);
  if (error) throw error;
  await insertAudit(compteId, AUDIT_ACTION_LABELS[actionKey], administrateur);
}

/**
 * Valide un compte PROPRIÉTAIRE (RPC SECURITY DEFINER serveur).
 *
 * Toute la logique sensible reste côté serveur : le serveur vérifie que
 * l'appelant est Super Admin, active le compte, puis applique l'offre de
 * lancement (essai gratuit de 6 mois, accès complet, une seule fois par
 * identité) dans la même transaction. L'audit est écrit par le serveur.
 *
 * Ce chemin est réservé aux propriétaires : le parcours des agences (validation
 * directe, inchangée) n'est pas impacté.
 */
export async function validerCompteProprietaire(compteId: string): Promise<void> {
  const { error } = await supabase.rpc("super_admin_valider_compte", { p_compte_id: compteId });
  if (error) throw error;
}

export async function updateCompteRecord(
  compteId: string,
  input: CompteInput,
  administrateur: string
): Promise<void> {
  // Seules des colonnes RÉELLES du schéma `comptes` sont écrites. Aucun champ
  // sensible (role, type_compte, auth_user_id) n'est envoyé : il reste protégé
  // côté serveur (RLS + trigger `proteger_compte`).
  const { error } = await supabase
    .from("comptes")
    .update({
      nom_complet: input.nom,
      email: input.email,
      telephone: input.telephone || null,
      formule_id: input.formuleId || null,
      statut: input.statut,
      actif: input.statut === "actif",
    })
    .eq("id", compteId);
  if (error) throw error;
  await insertAudit(compteId, AUDIT_ACTION_LABELS.modification, administrateur);
}

/* ------------------------------------------------------------------ */
/* Suppression définitive d'un compte (RPC SECURITY DEFINER serveur)   */
/* ------------------------------------------------------------------ */

export interface ApercuSuppressionCompte {
  compte: {
    id: string;
    nom: string;
    email: string;
    role: string;
    type_compte: string;
    statut: string;
    actif: boolean;
    formule: string;
  };
  est_super_admin: boolean;
  est_soi_meme: boolean;
  perimetre: Record<string, number>;
}

export interface ResultatSuppressionCompte {
  supprime: boolean;
  compte_id: string;
  email: string;
  nom: string;
  role: string;
  perimetre: Record<string, number>;
  documents_urls: string[];
  preuves_urls: string[];
}

const DOCUMENTS_BUCKET = "documents";
const PREUVES_BUCKET = "preuves-abonnements";

/**
 * Récupère — depuis Supabase — les informations réelles du compte ciblé et le
 * nombre d'éléments qui lui sont exclusivement rattachés. Lecture seule ; le
 * serveur refuse tout appelant qui n'est pas Super Admin actif.
 */
export async function apercuSuppressionCompte(compteId: string): Promise<ApercuSuppressionCompte> {
  const { data, error } = await supabase.rpc("apercu_suppression_compte", { p_compte_id: compteId });
  if (error) throw error;
  return data as ApercuSuppressionCompte;
}

/**
 * Supprime définitivement un compte et ses données EXCLUSIVES.
 *
 * Toute la suppression métier + `comptes` + `auth.users` + la trace d'audit est
 * exécutée côté serveur dans UNE transaction (RPC SECURITY DEFINER) : soit tout
 * est supprimé, soit rien (aucune suppression partielle).
 *
 * Le nettoyage du Storage privé est ensuite réalisé côté application à partir
 * des chemins réellement retournés par le serveur (best effort, jamais bloquant).
 */
export async function supprimerCompteSuperAdmin(compteId: string): Promise<ResultatSuppressionCompte> {
  const { data, error } = await supabase.rpc("supprimer_compte_super_admin", { p_compte_id: compteId });
  if (error) throw error;
  const resultat = data as ResultatSuppressionCompte;

  const documents = (resultat.documents_urls ?? []).filter((path) => Boolean(path));
  const preuves = (resultat.preuves_urls ?? []).filter((path) => Boolean(path));

  if (documents.length > 0) {
    const { error: docError } = await supabase.storage.from(DOCUMENTS_BUCKET).remove(documents);
    if (docError) console.error("[Super Admin] nettoyage Storage documents échoué", docError.message);
  }
  if (preuves.length > 0) {
    const { error: preuveError } = await supabase.storage.from(PREUVES_BUCKET).remove(preuves);
    if (preuveError) console.error("[Super Admin] nettoyage Storage preuves échoué", preuveError.message);
  }

  return resultat;
}